import { Button } from "@/components/ui/button"
import { Card } from "@/components/ui/card"
import { Link } from "react-router-dom"
import { motion } from "framer-motion"
import { ArrowRight, Wrench, Paintbrush, Sprout, Droplets, Zap, Hammer, Sparkles, Thermometer } from "lucide-react"

const services = [
  {
    icon: Wrench,
    title: "General Maintenance",
    description: "Comprehensive maintenance services for all your property needs",
    features: ["Regular inspections", "Preventive maintenance", "Emergency repairs"]
  },
  {
    icon: Paintbrush,
    title: "Painting Services",
    description: "Professional painting services for interior and exterior surfaces",
    features: ["Surface preparation", "Color consultation", "Quality finishes"]
  },
  {
    icon: Sprout,
    title: "Landscaping",
    description: "Complete landscaping and grounds maintenance services",
    features: ["Lawn care", "Tree trimming", "Garden maintenance"]
  },
  {
    icon: Droplets,
    title: "Plumbing",
    description: "Expert plumbing services for all types of properties",
    features: ["Leak repairs", "Installation", "Preventive maintenance"]
  },
  {
    icon: Zap,
    title: "Electrical",
    description: "Professional electrical services and maintenance",
    features: ["Wiring", "Lighting", "Safety inspections"]
  },
  {
    icon: Hammer,
    title: "Carpentry",
    description: "Skilled carpentry work for repairs and improvements",
    features: ["Repairs", "Installations", "Custom work"]
  },
  {
    icon: Sparkles,
    title: "Cleaning",
    description: "Professional cleaning services for all property types",
    features: ["Regular cleaning", "Deep cleaning", "Special services"]
  },
  {
    icon: Thermometer,
    title: "HVAC",
    description: "Complete HVAC maintenance and repair services",
    features: ["Maintenance", "Repairs", "Installation"]
  }
];

export function ServicesPage() {
  return (
    <div className="flex min-h-screen flex-col">
      <main className="flex-1">
        {/* Hero Section */}
        <section className="relative overflow-hidden container py-24 sm:py-32">
          {/* Background pattern */}
          <div className="absolute inset-0 -z-10 bg-grid-black/5 dark:bg-grid-white/5">
            <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-secondary/5"></div>
          </div>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="mx-auto flex max-w-[64rem] flex-col items-center text-center gap-4"
          >
            <div className="inline-flex items-center rounded-2xl glass-effect px-6 py-2 text-sm font-medium text-primary mb-4">
              <span className="block h-2 w-2 rounded-full bg-primary mr-2 animate-pulse" />
              Trusted by 500+ property owners
            </div>
            
            <h1 className="font-heading text-4xl font-bold sm:text-5xl md:text-6xl lg:text-7xl">
              Our <span className="bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">Services</span>
            </h1>
            <p className="max-w-[42rem] leading-normal text-muted-foreground sm:text-xl sm:leading-8">
              Comprehensive property maintenance services delivered by trusted professionals
            </p>
          </motion.div>
        </section>

        {/* Services Grid */}
        <section className="container py-12 sm:py-16">
          <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
            {services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Card variant="glass" className="h-full transition-all hover:shadow-lg hover:-translate-y-1 duration-300">
                  <div className="flex h-full flex-col justify-between p-6">
                    <div>
                      <div className="flex h-14 w-14 items-center justify-center rounded-full bg-primary/10 mb-4">
                        <service.icon className="h-7 w-7 text-primary" />
                      </div>
                      <h3 className="text-xl font-bold">{service.title}</h3>
                      <p className="mt-2 text-muted-foreground">
                        {service.description}
                      </p>
                      <ul className="mt-6 space-y-3">
                        {service.features.map((feature, idx) => (
                          <li key={idx} className="flex items-center text-sm">
                            <div className="mr-2 h-4 w-4 rounded-full bg-primary/10 flex items-center justify-center">
                              <ArrowRight className="h-2.5 w-2.5 text-primary" />
                            </div>
                            {feature}
                          </li>
                        ))}
                      </ul>
                    </div>
                    <Link to="/register" className="mt-6">
                      <Button variant="default" className="w-full mt-6">Book Service</Button>
                    </Link>
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>
        </section>

        {/* CTA Section */}
        <section className="relative overflow-hidden">
          <div className="absolute inset-0 -z-10 bg-primary/5"></div>
          <div className="container py-24 sm:py-32">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="mx-auto flex max-w-[58rem] flex-col items-center justify-center gap-4 text-center"
            >
              <Card variant="glass" className="w-full p-8">
                <h2 className="font-heading text-3xl font-bold leading-[1.1] sm:text-4xl md:text-5xl">
                  Need a custom maintenance solution?
                </h2>
                <p className="max-w-[85%] mx-auto mt-4 leading-normal text-muted-foreground sm:text-lg sm:leading-7">
                  Contact our team to discuss your specific requirements
                </p>
                <Link to="/contact">
                  <Button size="lg" className="mt-8 animate-pulse-subtle">
                    Contact Us
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </Card>
            </motion.div>
          </div>
        </section>
      </main>
    </div>
  )
}